import { useLazyQuery, useQuery } from "@apollo/client";
import {
  Card,
  Drawer,
  LoadingOverlay,
  ScrollArea,
  useMantineTheme,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { SUPPLIER_TRANSACTIONS } from "apollo/queries";
import ActionButton from "components/controls/ActionButtonNew";
import TotalAmount from "components/controls/TotalAmount";
import B2bTable from "components/reusable/b2bTable";
import ReportFilters from "components/reusable/ReportFilters";
import { customLoader } from "components/utilities/loader";
import { useEffect, useState } from "react";
import { FiEye } from "react-icons/fi";
import { formatLocalDate, formatNumber } from "utiles/url";
import BussinessTable from "./bussinessCommistion";

const SupplierTransactions = () => {
  const [size, setSize] = useState("10"); // Default page size
  const [total, setTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [hasMounted, setHasMounted] = useState(false);
  const [timeRange, setTimeRange] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [editId, setEditId] = useState();
  const [openedDetail, setOpenedDetail] = useState(false);
  const [detailUserName, setDetailUserName] = useState(null);
  const [detailTotalAmount, setDetailTotalAmount] = useState(null);

  const theme = useMantineTheme();
  const [confirmedSearch, setConfirmedSearch] = useState("");

  const clearInput = () => {
    setConfirmedSearch("");
  };
  const { data, loading, fetchMore, refetch } = useQuery(
    SUPPLIER_TRANSACTIONS,
    {
      variables: {
        first: Number.parseInt(size, 10),
        page: activePage,
        search: confirmedSearch,
        ordered_by: [
          {
            column: "CREATED_AT",
            order: "DESC",
          },
        ],
        period: timeRange || undefined,
        start_date: startDate ? formatLocalDate(startDate) : undefined,
        end_date: endDate ? formatLocalDate(endDate) : undefined,
      },
      onError(error) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message ||
            "Failed to get supplier commistions",
        });
      },
    },
  );

  const [fetchAllSupplierTransactions] = useLazyQuery(SUPPLIER_TRANSACTIONS);

  const handleExportDataFetch = async () => {
    const { data: allData } = await fetchAllSupplierTransactions({
      variables: {
        first: null,
        page: 1,
        search: confirmedSearch,
        ordered_by: [
          {
            column: "CREATED_AT",
            order: "DESC",
          },
        ],
        period: timeRange || undefined,
        start_date: startDate ? formatLocalDate(startDate) : undefined,
        end_date: endDate ? formatLocalDate(endDate) : undefined,
      },
    });
    return allData?.getSupplierCommissionTransactions?.data || [];
  };

  useEffect(() => {
    setHasMounted(true);
  }, []);
  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };
  useEffect(() => {
    if (data) {
      setTotal(data.getSupplierCommissionTransactions.paginatorInfo.lastPage);
    }
  }, [data, size]);
  useEffect(() => {
    refetch();
  }, [refetch]);
  const handleChange = (currentPage) => {
    fetchMore({
      variables: {
        first: Number.parseInt(size, 10),
        page: currentPage,
      },
    });
    setActivePage(currentPage);
  };
  if (!hasMounted) {
    return null;
  }
  const handleViewDetails = (rowData) => {
    setOpenedDetail(true);
    setEditId(rowData.businesses);
    setDetailUserName(rowData.supplier?.user?.name || null);
    setDetailTotalAmount(rowData.total_commission ?? null);
  };
  const headerData = [
    {
      label: "#",
      key: "index",
      sortable: false,
      searchable: false,
      width: "5%",
      render: (_rowData, rowIndex) => {
        const idx =
          (activePage - 1) * Number.parseInt(size || "0", 10) + (rowIndex + 1);
        return <span>{idx}</span>;
      },
    },
    {
      label: "Supplier Name",
      key: "amount",
      sortable: false,
      searchable: false,
      width: "20%",
      render: (rowData) => {
        return (
          <span style={{ width: "120px" }}>{rowData.supplier.user.name}</span>
        );
      },
    },
    {
      label: "Supplier Phone",
      key: "amount",
      sortable: false,

      searchable: false,
      render: (rowData) => {
        return (
          <span style={{ width: "120px" }}>{rowData?.supplier.user.phone}</span>
        );
      },
    },
    {
      label: "Supplier Email",
      key: "amount",
      sortable: false,
      width: "20%",
      searchable: false,
      render: (rowData) => {
        return (
          <span style={{ width: "120px" }}>{rowData?.supplier.user.email}</span>
        );
      },
    },
    {
      label: "Amount",
      key: "amount",
      sortable: true,
      searchable: false,
      width: "8%",
      render: (rowData) => {
        return <span>{formatNumber(rowData.total_commission)}</span>;
      },
    },
    {
      label: "Actions",
      key: "actions",
      sortable: false,
      searchable: false,
      exportable: false,
      render: (rowData) => {
        return (
          <div style={{ display: "flex", gap: "6px" }}>
            <ActionButton
              label="View"
              color="success"
              onClick={() => handleViewDetails(rowData)}
            >
              <FiEye fontSize="medium" />
            </ActionButton>
          </div>
        );
      },
    },
  ];

  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <div style={{ width: "98%", margin: "auto" }}>
      <ReportFilters
        data={data ? data.getSupplierCommissionTransactions.data : []}
        headers={headerData}
        reportTitle="Supplier Commissions Report"
        filterSubtitle="All Supplier Commissions"
        fileNamePrefix="supplier_commissions_report"
        onFetch={handleExportDataFetch}
        onTimeRangeChange={setTimeRange}
        onDateRangeChange={({ startDate, endDate }) => {
          setStartDate(startDate);
          setEndDate(endDate);
        }}
        timeRange={timeRange}
        startDate={startDate}
        endDate={endDate}
      />
      <Drawer
        opened={openedDetail}
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
        padding="xl"
        onClose={() => setOpenedDetail(false)}
        position="bottom"
        size="80%"
      >
        <BussinessTable
          ordersList={editId}
          userName={detailUserName}
          totalAmount={detailTotalAmount}
        />
      </Drawer>

      <Card shadow="sm" p="lg">
        <ScrollArea>
          <B2bTable
            total={total}
            searchInput={false}
            activePage={activePage}
            clearInput={clearInput}
            handelSearch={setConfirmedSearch}
            searchValue={confirmedSearch}
            onSearchChange={setConfirmedSearch}
            handleChange={handleChange}
            header={headerData}
            loading={loading}
            data={data ? data.getSupplierCommissionTransactions.data : []}
            handlePageSizeChange={handlePageSizeChange}
            size={size}
          />
          <TotalAmount
            amount={
              data
                ? data.getSupplierCommissionTransactions
                    .all_suppliers_total_commission
                : 0
            }
          />
        </ScrollArea>
      </Card>
    </div>
  );
};

export default SupplierTransactions;
